// Salary allocation plans (payroll-service, Increment B). Payroll owns the
// split; employee-service owns the destinations it points at. Every failure
// carries a backend message — surface it with getApiErrorMessage.
import { useMutation, useQuery } from '@tanstack/react-query';
import { gatewayApi } from '@/lib/hrApi';
import { queryClient } from '@/lib/queryClient';

const BASE = '/api/v1/payroll';

export type AllocationPlanStatus =
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'superseded';

export interface AllocationLine {
  seq: number;
  payment_method_id: string;
  method_type: 'BANK' | 'MOBILE_MONEY';
  masked: string;
  percent_bp: number;
}

export interface AllocationPlan {
  id: string;
  employee_id: string;
  effective_from: string;
  status: AllocationPlanStatus;
  note?: string;
  lines: AllocationLine[];
  proposed_by?: string;
  proposed_at: string;
  decided_by?: string;
  decided_at?: string;
  decision_reason?: string;
}

export interface AllocationDestination {
  id: string;
  type: 'BANK' | 'MOBILE_MONEY';
  provider?: string;
  masked: string;
  currency: string;
  is_default: boolean;
  status: string;
}

export interface SalaryAllocationView {
  plans: AllocationPlan[];
  effective: AllocationPlan | null;
  pending: AllocationPlan | null;
  destinations: AllocationDestination[] | null;
  destinations_status: 'ok' | 'unavailable';
  stale_destination_ids: string[];
}

export interface ProposeAllocationInput {
  effective_from: string;
  note?: string;
  lines: { payment_method_id: string; percent_bp: number }[];
}

export const getSalaryAllocation = (employeeId: string) =>
  gatewayApi.get<SalaryAllocationView>(`${BASE}/employees/${employeeId}/salary-allocation`).then((r) => r.data);
export const proposeSalaryAllocation = (employeeId: string, body: ProposeAllocationInput) =>
  gatewayApi.post<AllocationPlan>(`${BASE}/employees/${employeeId}/salary-allocation`, body).then((r) => r.data);
export const listPendingSalaryAllocations = () =>
  gatewayApi.get<AllocationPlan[]>(`${BASE}/salary-allocations/pending`).then((r) => r.data);
export const approveSalaryAllocation = (id: string) =>
  gatewayApi.post<AllocationPlan>(`${BASE}/salary-allocations/${id}/approve`).then((r) => r.data);
export const rejectSalaryAllocation = (id: string, reason: string) =>
  gatewayApi.post<AllocationPlan>(`${BASE}/salary-allocations/${id}/reject`, { reason }).then((r) => r.data);
export const cancelSalaryAllocation = (id: string, reason: string) =>
  gatewayApi.post<AllocationPlan>(`${BASE}/salary-allocations/${id}/cancel`, { reason }).then((r) => r.data);

const KEYS = {
  employee: (id: string) => ['salary-allocation', 'employee', id] as const,
  pending: ['salary-allocation', 'pending'] as const,
};
const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['salary-allocation'] });

export function useSalaryAllocation(employeeId?: string | null) {
  return useQuery({
    queryKey: KEYS.employee(employeeId || ''),
    queryFn: () => getSalaryAllocation(employeeId as string),
    enabled: !!employeeId,
  });
}

export function usePendingSalaryAllocations(enabled = true) {
  return useQuery({ queryKey: KEYS.pending, queryFn: listPendingSalaryAllocations, enabled });
}

export function useProposeSalaryAllocation(employeeId: string) {
  return useMutation({
    mutationFn: (body: ProposeAllocationInput) => proposeSalaryAllocation(employeeId, body),
    onSuccess: invalidate,
  });
}

export function useDecideSalaryAllocation() {
  return useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: 'approve' | 'reject' | 'cancel'; reason?: string }) =>
      action === 'approve'
        ? approveSalaryAllocation(id)
        : action === 'reject'
          ? rejectSalaryAllocation(id, reason || '')
          : cancelSalaryAllocation(id, reason || ''),
    onSuccess: invalidate,
  });
}
